//* react
import React from 'react';

//* material-ui
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Fade,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

//* types
import { MenuSingleTemplateProps, TemplateErrors, TemplateVarsData } from 'types/templates/templates-menu';
import {
  WhatsappFrontTemplateObject,
  WhatsappOriginalTemplateBody,
  WhatsappOriginalTemplateHeader,
} from 'types/templates';

//* icons
import SendIcon from '@mui/icons-material/Send';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';

//* system
import TemplateInfo from './TemplateInfo';
import TemplatePreview from './TemplatePreview';
import { fetchTemplateVarsData } from 'api/templates';
import { synthesizeWhatsappTemplateFromOriginal } from 'utils/synthesize-whatsapp-template';
import { buildTemplateVarsDataFromOriginal } from 'utils/build-template-vars-from-original';
import TemplateVars from './TemplateVars';
import useLocalization from 'hooks/useLocalization';
import { checkIsTemplateValidWithVarsData } from 'utils/chat';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';
import { buildDraftTemplateSendParamsFallback, formSendTemplateObject } from 'utils/helpers';
import { useSelector } from 'store';
import { getTemplateDefaultFile } from 'store/slices/templates';
import { scheduleMessage, notifyScheduledMessagesChanged } from 'api/scheduled-messages';
import ComposerScheduleMenu from 'views/chats/singleChat/chat/actions/send-message-area/ComposerScheduleMenu';
import { formatScheduleDisplayHe } from 'utils/chat-composer-schedule';
import {
  TEMPLATES_MENU_DURATION,
  TEMPLATES_MENU_EASING_CSS,
  templatesMenuListRowDelayMs,
} from '../templates-menu-motion';
import { templateMenuListRowSurfaceSx } from '../template-menu-list-row-surface';

const MenuSingleTemplate: React.FC<MenuSingleTemplateProps> = ({
  template,
  originalTemplate,
  selected = false,
  onSelect = () => {},
  isHebrew,
  resetTemplate,
  onSaveHebrewDisplayName,
  noUpload,
  chatUser,
  sendable,
  onSendTemplate,
  onClose,
  onSelectedTemplateChange,
  disablePadding,
  initialSelectedTemplate,
  disableMediaSelection,
  disableParams,
  usePredefinedParams,
  predefinedParamsList,
  predefinedChangeAction,
  predefinedExampleData,
  predefinedSelectedColumns,
  onFileNameChange,
  initialMediaFile,
  listIndex,
  showSecondaryTemplateName = false,
  showMarketing,
  showService,
  compact = false,
  templatePreviewWhatsappStyle = false,
  embeddedWizardPreview = false,
  confirmApply = false,
  afterConfirmApply,
  disableListRowEnterAnimation = false,
  lockExpandedCardLayout = false,
  focusedConfigurationMode = false,
  listItemRef,
  maxVideoFileSizeBytes,
  templateExtraVariables,
  templateExtraVariablesSectionLabel,
  broadcastWizardParams,
}) => {
  //* localization
  const { currentLocale } = useLocalization();
  const { localization } = React.useMemo(
    () => ({
      localization: currentLocale.templatesMenu,
    }),
    [currentLocale],
  );

  const { enqueueSnackbar } = useSnackbar();

  const hebrewName = template?.hebrewName || null;

  /** כשאין התאמה ב־store — עדיין טוענים פרמטרים לפי originalTemplate */
  const effectiveTemplate = React.useMemo(
    () => template ?? synthesizeWhatsappTemplateFromOriginal(originalTemplate),
    [template, originalTemplate],
  );

  //* קובץ ברירת המחדל שהוגדר לתבנית אצלנו — ממלא מראש את כותרת המדיה בשליחה;
  //* prop מפורש (למשל מסמך פריוריטי) גובר תמיד, והנציג יכול להחליף את הקובץ.
  const templateDefaultFiles = useSelector((state) => state.templates.defaultFiles);
  const defaultHeaderFile = React.useMemo(
    () => getTemplateDefaultFile(templateDefaultFiles, originalTemplate?.name, originalTemplate?.language),
    [templateDefaultFiles, originalTemplate],
  );
  const effectiveInitialMediaFile = initialMediaFile ?? defaultHeaderFile?.fileUrl;

  /** עדכון רפרנס בלי לשנות זהות useCallback — מונע לולאת fetch+סנכרון כשההורה מעבירה אותו templateObject עם reference חדש */
  const initialSelectedTemplateRef = React.useRef(initialSelectedTemplate);
  const initialMediaFileRef = React.useRef(effectiveInitialMediaFile);
  const defaultHeaderFileRef = React.useRef(defaultHeaderFile);
  React.useEffect(() => {
    initialSelectedTemplateRef.current = initialSelectedTemplate;
    initialMediaFileRef.current = effectiveInitialMediaFile;
    defaultHeaderFileRef.current = defaultHeaderFile;
  }, [initialSelectedTemplate, initialMediaFile, effectiveInitialMediaFile, defaultHeaderFile]);

  const { templateHeader, templateBody } = React.useMemo<{
    templateHeader: WhatsappOriginalTemplateHeader | undefined;
    templateBody: WhatsappOriginalTemplateBody | undefined;
    // templateFooter: WhatsappOriginalTemplateFooter | undefined;
    // templateButtons: WhatsappOriginalTemplateButtons | undefined;
  }>(() => {
    const templateHeader = originalTemplate.components?.find((comp) => comp.type === 'HEADER');
    const templateBody = originalTemplate.components?.find((comp) => comp.type === 'BODY');
    // const templateFooter = originalTemplate.components?.find((comp) => comp.type === 'FOOTER');
    // const templateButtons = originalTemplate.components?.find((comp) => comp.type === 'BUTTONS');

    return {
      templateHeader,
      templateBody,
      // templateFooter,
      // templateButtons,
    };
  }, [originalTemplate]);

  const [varsData, setVarsData] = React.useState<TemplateVarsData>([]);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [thisTemplateLoading, setThisTemplateLoading] = React.useState<boolean>(false);
  const [templateErrors, setTemplateErrors] = React.useState<TemplateErrors>({});
  const [sendingTemplate, setSendingTemplate] = React.useState<boolean>(false);

  const mediaFile = React.useMemo(() => {
    const headerMediaParam = varsData.find((param) => param.name === 'data');
    if (headerMediaParam) {
      return {
        type: headerMediaParam.label,
        url: headerMediaParam.text,
      };
    } else return null;
  }, [varsData]);

  const getTemplateVarsData = React.useCallback(
    async function () {
      if (!selected) return;
      setFileName(null);
      setThisTemplateLoading(true);
      const data = await fetchTemplateVarsData(effectiveTemplate);
      let specList: TemplateVarsData = Array.isArray(data?.spec) ? (data.spec as TemplateVarsData) : [];
      const fallbackList = buildTemplateVarsDataFromOriginal(originalTemplate);
      if (specList.length === 0) {
        specList = fallbackList;
      } else if (fallbackList.length > 0) {
        const existingNames = new Set(specList.map((param) => param.name));
        for (const param of fallbackList) {
          if (!existingNames.has(param.name)) {
            specList.push(param);
          }
        }
      }
      if (specList.length > 0) {
        let newVarsData = specList.map((param) => ({
          ...param,
          text: param.name === 'data' && initialMediaFileRef.current ? initialMediaFileRef.current : '',
        }));
        //* כשהקובץ הממולא הוא ברירת המחדל של התבנית — גם שם הקובץ (fn במסמכים) מגיע ממנה
        const appliedDefault = defaultHeaderFileRef.current;
        if (
          appliedDefault &&
          appliedDefault.fileName &&
          initialMediaFileRef.current === appliedDefault.fileUrl &&
          newVarsData.some((param) => param.name === 'data')
        ) {
          setFileName(appliedDefault.fileName);
        }
        const initialSel = initialSelectedTemplateRef.current;
        if (
          initialSel &&
          initialSel.templateName === effectiveTemplate.name &&
          initialSel.components
        ) {
          // Build frontTemplateObj from components array for backward compatibility
          const frontTemplateObj: WhatsappFrontTemplateObject = {
            messaging_product: 'whatsapp',
            to: '',
            type: 'template',
            template: {
              name: initialSel.templateName,
              language: { code: initialSel.languageCode },
              components: initialSel.components as any,
            },
          };
          let buttonsIndex = 1;
          frontTemplateObj.template.components.forEach((comp) => {
            if (comp.type === 'HEADER' && comp.parameters[0].type !== 'text') {
              newVarsData = newVarsData.map((param) => {
                if (param.name === 'data') {
                  if (comp.parameters[0].type === 'image') {
                    return { ...param, text: comp.parameters[0].image?.link || '' };
                  } else if (comp.parameters[0].type === 'video') {
                    return { ...param, text: comp.parameters[0].video?.link || '' };
                  } else if (comp.parameters[0].type === 'document') {
                    return { ...param, text: comp.parameters[0].document?.link || '' };
                  }
                }
                return param;
              });
            } else if (comp.type === 'button') {
              const buttonsParam = newVarsData.find((param) => param.name === 'buttons_' + buttonsIndex);
              if (buttonsParam) {
                buttonsParam.text = comp.parameters[0].text || '';
                buttonsIndex++;
              }
            } else {
              const params = comp.parameters;
              params.forEach((param, index) => {
                const paramName = comp.type.toLowerCase() + '_' + (index + 1);
                const foundParam = newVarsData.find((item) => item.name === paramName);
                if (foundParam) {
                  if (param.type === 'text') {
                    foundParam.text = param.text || '';
                  }
                }
              });
            }
          });
        }
        setVarsData(newVarsData);
      }
      setThisTemplateLoading(false);
    },
    [effectiveTemplate, selected, originalTemplate],
  );

  const handleTemplateErrors = React.useCallback((params: Partial<TemplateErrors>) => {
    setTemplateErrors((prevObj) => ({ ...prevObj, ...params }));
  }, []);

  //* Template edit / send events
  // const formSendTemplateObject = React.useCallback(
  //   async (to: string) => {
  //     try {
  //       const formattedParams: Record<TemplateVarsData[0]['name'], string> = varsData.reduce(
  //         (acc, param) => {
  //           acc[param.name] = param.text || '';
  //           return acc;
  //         },
  //         {} as Record<TemplateVarsData[0]['name'], string>,
  //       );

  //       const params: GetTemplateFormattedObjectParams = {
  //         templateObject: template,
  //         templateOriginalObject: originalTemplate,
  //         to: to,
  //         templateVars: formattedParams,
  //       };

  //       if (Object.keys(formattedParams).some((objKey) => objKey === 'data') && fileName) {
  //         params.fileName = fileName;
  //       }

  //       const object = await getTemplateFormattedObject(params);

  //       if (object) {
  //         const { frontTemplateObj, components } = object;
  //         const storeState = store.getState();

  //         let sendParams: FetchSendTemplateMessageParams = {
  //           to: to,
  //           templateName: frontTemplateObj.template.name,
  //           languageCode: frontTemplateObj.template.language.code,
  //           components: JSON.stringify(components),
  //           frontTemplateObj: JSON.stringify(frontTemplateObj),
  //           userglobalid: storeState.user?.Udid as string,
  //           templateText: '',
  //           isVars: varsData.length > 0,
  //         };

  //         return { sendParams, frontTemplateObj, components, formattedParams };
  //       } else {
  //         return null;
  //       }
  //     } catch (error) {
  //       console.warn('formSendTemplateObject error: ', error);
  //       return null;
  //     }
  //   },
  //   [fileName, originalTemplate, template, varsData],
  // );
  const submitSendTemplate = React.useCallback(async () => {
    const { valid, details } = checkIsTemplateValidWithVarsData(varsData);
    handleTemplateErrors(details);

    if (valid) {
      setSendingTemplate(true);
      const objectParams = await formSendTemplateObject(
        chatUser?.UserDisplayPhoneNumber || '',
        varsData,
        effectiveTemplate,
        originalTemplate,
        fileName || undefined,
      );
      if (objectParams) {
        const { sendParams, frontTemplateObj, components, formattedParams } = objectParams;
        if (onSendTemplate) onSendTemplate(sendParams, frontTemplateObj, components, formattedParams);
        if (onClose) onClose();
      } else {
        enqueueSnackbar('שגיאה בשליחת התבנית', {
          variant: 'error',
        });
      }

      setSendingTemplate(false);
    }
  }, [
    chatUser?.UserDisplayPhoneNumber,
    enqueueSnackbar,
    fileName,
    handleTemplateErrors,
    onClose,
    onSendTemplate,
    originalTemplate,
    effectiveTemplate,
    varsData,
  ]);
  //* תזמון שליחת תבנית (19.10): במקום השליחה הישירה ל-C#, קריאה לנוד עם שם
  //* התבנית, המשתנים וקובץ הכותרת — הצרכן בשרת שולח בזמן שנקבע. תבנית מאושרת
  //* לא כפופה לחלון 24 השעות, ולכן התפריט נפתח בלי תקרה (unboundedWindow).
  const [scheduleAnchor, setScheduleAnchor] = React.useState<HTMLElement | null>(null);
  const scheduleLoc = currentLocale.chat?.inputArea?.scheduleSend;

  const submitScheduleTemplate = React.useCallback(
    async (at: Date) => {
      const { valid, details } = checkIsTemplateValidWithVarsData(varsData);
      handleTemplateErrors(details);
      if (!valid) return;

      const phone = chatUser?.UserDisplayPhoneNumber;
      const templateName = effectiveTemplate?.name || originalTemplate?.name;
      if (!phone || !templateName) return;

      //* מיפוי משתני הדיאלוג לחוזה הנוד: header_1 / body_N / button_N + קובץ כותרת
      const templateParameters: Record<string, string> = {};
      let headerFileLink: string | undefined;
      for (const param of varsData) {
        if (!param.name) continue;
        if (param.name === 'data') {
          headerFileLink = param.text || undefined;
          continue;
        }
        const key = param.name.startsWith('buttons_')
          ? param.name.replace('buttons_', 'button_')
          : param.name;
        if (/^(header_1|body_\d+|button_\d+)$/.test(key) && param.text) {
          templateParameters[key] = param.text;
        }
      }

      setSendingTemplate(true);
      try {
        await scheduleMessage({
          phone,
          scheduledSendTime: at.toISOString(),
          templateName,
          ...(Object.keys(templateParameters).length ? { templateParameters } : {}),
          ...(headerFileLink ? { headerFileLink } : {}),
          ...(fileName ? { headerFileName: fileName } : {}),
        });
        notifyScheduledMessagesChanged();
        enqueueSnackbar(`התבנית תוזמנה לשליחה ב-${formatScheduleDisplayHe(at)}`, { variant: 'success' });
        if (onClose) onClose();
      } catch (error) {
        console.warn('scheduleTemplate err: ', error);
        enqueueSnackbar('תזמון התבנית נכשל — נסו שוב', { variant: 'error' });
      } finally {
        setSendingTemplate(false);
      }
    },
    [
      chatUser?.UserDisplayPhoneNumber,
      effectiveTemplate,
      enqueueSnackbar,
      fileName,
      handleTemplateErrors,
      onClose,
      originalTemplate,
      varsData,
    ],
  );

  const templateChangeAction = React.useCallback(async (): Promise<boolean> => {
    if (!onSelectedTemplateChange || !selected) return false;
    let objectParams = await formSendTemplateObject(
      '',
      varsData,
      effectiveTemplate,
      originalTemplate,
      fileName || undefined,
    );
    if (!objectParams && confirmApply) {
      objectParams = buildDraftTemplateSendParamsFallback('', originalTemplate) as NonNullable<typeof objectParams>;
    }
    if (!objectParams) return false;
    const { sendParams, frontTemplateObj, components } = objectParams;
    const { valid, details } = checkIsTemplateValidWithVarsData(varsData, true);

    handleTemplateErrors(details);
    onSelectedTemplateChange({
      sendObject: sendParams,
      frontTemplateObject: frontTemplateObj,
      components,
      valid: disableParams ? true : valid,
      varsData,
    });
    /* confirmApply: תמיד מחזירים הצלחה אחרי שמירה — גם בטיוטה לפני השלמת פרמטרים */
    if (confirmApply) return true;
    return disableParams ? true : valid;
  }, [
    confirmApply,
    disableParams,
    fileName,
    handleTemplateErrors,
    onSelectedTemplateChange,
    originalTemplate,
    selected,
    effectiveTemplate,
    varsData,
  ]);

  //* Debounced event for template action to prevent unnecessary API calls
  const debouncedTemplateChangeAction = useDebouncedCallback(templateChangeAction, 1500);

  /** מונע קריאות חוזרות ל־GetTemplateFormattedObject כש־varsData / סנכרון לא באמת השתנו */
  const lastTemplateSyncKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    lastTemplateSyncKeyRef.current = null;
  }, [originalTemplate.id, originalTemplate.name]);

  const handleConfirmApply = React.useCallback(async () => {
    const ok = await templateChangeAction();
    if (ok) afterConfirmApply?.();
  }, [afterConfirmApply, templateChangeAction]);

  React.useEffect(() => {
    if (!selected) return;
    if (confirmApply) return;

    const syncKey = JSON.stringify({
      n: effectiveTemplate?.name,
      vk: varsData.map((v) => [v.name, v.text ?? '']),
      fn: fileName ?? '',
      disableParams,
    });

    if (disableParams) {
      if (lastTemplateSyncKeyRef.current === syncKey) return;
      lastTemplateSyncKeyRef.current = syncKey;
      void templateChangeAction();
      return;
    }

    debouncedTemplateChangeAction();
  }, [
    selected,
    confirmApply,
    disableParams,
    debouncedTemplateChangeAction,
    effectiveTemplate?.name,
    fileName,
    templateChangeAction,
    varsData,
  ]);

  /** אחרי שני rAF — מאפשר לדפדפן לצבוע את המגירה/הרשימה לפני רשת + עיבוד כבד */
  React.useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) void getTemplateVarsData();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [getTemplateVarsData, selected]);
  React.useEffect(() => {
    const handleSendOnEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        submitSendTemplate();
      }
    };

    if (selected) {
      document.addEventListener('keydown', handleSendOnEnter as EventListener);
    }

    return () => {
      document.removeEventListener('keydown', handleSendOnEnter);
    };
  }, [selected, submitSendTemplate]);

  React.useEffect(() => {
    onFileNameChange?.(fileName);
  }, [fileName, onFileNameChange]);

  /** לחיצה על שורת הכותרת: פותחת או סוגרת את האקורדיון; לא מפריעה לכפתורים/שדות בתוך הכותרת */
  const handleTemplateHeaderClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (focusedConfigurationMode) return;
      const t = e.target as HTMLElement;
      if (t.closest('button, a, input, textarea, select, [role="combobox"]')) return;
      if (!originalTemplate.id) return;
      if (selected) resetTemplate();
      else onSelect(originalTemplate.id);
    },
    [focusedConfigurationMode, onSelect, originalTemplate.id, resetTemplate, selected],
  );

  const isExpanded = focusedConfigurationMode || selected;

  return (
    <ListItem
      ref={listItemRef as React.Ref<HTMLLIElement>}
      sx={{
        px: 0,
        py: 0,
        mb: compact ? 0.25 : focusedConfigurationMode ? 0 : 0.5,
        display: 'block',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
        ...(listIndex != null &&
          !disableListRowEnterAnimation && {
          '@keyframes templatesMenuRowEnter': {
            from: {
              opacity: 0,
              transform: 'translate3d(0, 6px, 0)',
            },
            to: {
              opacity: 1,
              transform: 'translate3d(0, 0, 0)',
            },
          },
          animation: `templatesMenuRowEnter ${TEMPLATES_MENU_DURATION.collapseEnter}ms ${TEMPLATES_MENU_EASING_CSS} ${templatesMenuListRowDelayMs(listIndex)}ms both`,
        }),
      }}
    >
      <ListItemButton
        selected={selected}
        className="template-menu-card"
        sx={(th: Theme): SystemStyleObject<Theme> => ({
          ...(focusedConfigurationMode
            ? {}
            : (templateMenuListRowSurfaceSx(th) as SystemStyleObject<Theme>)),
          p: compact ? th.spacing(0.5) : th.spacing(focusedConfigurationMode ? 0 : 1),
          borderRadius: focusedConfigurationMode ? 0 : 1,
          cursor: focusedConfigurationMode || selected ? 'default' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          /* ברשימה נגללת במגירה — ברירת מחדל של ListItemButton חותכת את ה-Collapse/כפתור התחתון */
          ...(isExpanded && { overflow: 'visible' }),
          /**
           * התאמת ביצועים למגירת הגדרות:
           * מעטפת כרטיס בגובה יציב + גלילה פנימית לתוכן המורחב,
           * כדי לצמצם reflow/repaint כבדים בזמן החלפת מודולים.
           */
          ...(isExpanded &&
            lockExpandedCardLayout && {
              overflow: 'hidden',
              maxHeight: 'min(600px, calc(100vh - 260px))',
            }),
        })}
      >
        <Stack
          direction={'column'}
          width="100%"
          sx={
            isExpanded && lockExpandedCardLayout
              ? { minHeight: 0, flex: '1 1 auto', overflow: 'hidden' }
              : undefined
          }
        >
          {!focusedConfigurationMode && (
          <Box
            onClick={handleTemplateHeaderClick}
            sx={{ cursor: 'pointer' }}
          >
            {/* Template Info */}
            <TemplateInfo
              originalTemplate={originalTemplate}
              template={template}
              selected={selected}
              isHebrew={isHebrew}
              hebrewName={hebrewName}
              templateHeader={templateHeader}
              resetTemplate={resetTemplate}
              onSaveHebrewDisplayName={onSaveHebrewDisplayName}
              showSecondaryTemplateName={showSecondaryTemplateName}
              showMarketing={showMarketing}
              showService={showService}
              compact={compact}
            />
            {/* Template text preview */}
            <Typography
              variant={compact ? 'caption' : 'body2'}
              color="text.secondary"
              sx={{
                mt: compact ? 0.25 : 0.5,
                lineHeight: compact ? 1.25 : 1.43,
                ...(!compact && { fontSize: 13 }),
              }}
            >
              {templateBody?.text && templateBody.text.length > 45
                ? templateBody?.text.slice(0, 45).replace(/^[a-z\u05D0-\u05EA]+$/i, '') + '...'
                : templateBody?.text}
            </Typography>
          </Box>
          )}

          <Collapse
            in={isExpanded}
            timeout={
              embeddedWizardPreview || focusedConfigurationMode
                ? { enter: 0, exit: 0 }
                : {
                    enter: TEMPLATES_MENU_DURATION.collapseEnter,
                    exit: TEMPLATES_MENU_DURATION.collapseExit,
                  }
            }
            unmountOnExit={!focusedConfigurationMode}
          >
            <Box
              className="template-menu-card__content"
              sx={{
                pt: compact ? 0.75 : focusedConfigurationMode ? 0 : 1,
                pb: confirmApply && !sendable ? 0.5 : 0,
                ...(isExpanded &&
                  lockExpandedCardLayout && {
                    minHeight: 0,
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }),
              }}
            >
              <Stack direction={'column'} spacing={embeddedWizardPreview ? 1.25 : 3}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: templatePreviewWhatsappStyle ? 2 : 0,
                    ...(embeddedWizardPreview && { flexShrink: 0 }),
                  }}
                >
                  <TemplatePreview
                    originalTemplate={originalTemplate}
                    varsData={varsData}
                    mediaFile={mediaFile}
                    noUpload={noUpload}
                    whatsappStyle={templatePreviewWhatsappStyle}
                  />
                  {embeddedWizardPreview && thisTemplateLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: templatePreviewWhatsappStyle ? 2 : 1,
                        bgcolor: (t) => alpha(t.palette.background.paper, 0.65),
                        backdropFilter: 'blur(6px)',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={32} color="secondary" />
                    </Box>
                  )}
                </Box>

                {thisTemplateLoading && !embeddedWizardPreview ? (
                  <Fade in timeout={TEMPLATES_MENU_DURATION.fade} unmountOnExit>
                    <Box
                      width="100%"
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 50,
                      }}
                    >
                      <CircularProgress size={32} color="secondary" />
                    </Box>
                  </Fade>
                ) : disableParams && !sendable && !confirmApply ? null : (
                  <Fade in timeout={TEMPLATES_MENU_DURATION.fadeSlow}>
                    <Stack direction={'column'} spacing={3}>
                      {!disableParams && (
                        <Box position="relative">
                          {thisTemplateLoading && embeddedWizardPreview && varsData.length === 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                bgcolor: (t) => alpha(t.palette.background.paper, 0.65),
                                backdropFilter: 'blur(4px)',
                                zIndex: 1,
                              }}
                            >
                              <CircularProgress size={24} color="secondary" />
                            </Box>
                          )}
                          <TemplateVars
                            varsData={varsData}
                            setVarsData={setVarsData}
                            setFileName={setFileName}
                            chatUser={chatUser}
                            templateErrors={templateErrors}
                            handleTemplateErrors={handleTemplateErrors}
                            disableMediaSelection={disableMediaSelection}
                            usePredefinedParams={usePredefinedParams}
                            predefinedParamsList={predefinedParamsList}
                            predefinedChangeAction={predefinedChangeAction}
                            predefinedExampleData={predefinedExampleData}
                            predefinedSelectedColumns={predefinedSelectedColumns}
                            initialMediaFile={initialMediaFile}
                            maxVideoFileSizeBytes={maxVideoFileSizeBytes}
                            extraVariables={templateExtraVariables}
                            extraVariablesSectionLabel={templateExtraVariablesSectionLabel}
                            broadcastWizardParams={broadcastWizardParams ?? embeddedWizardPreview}
                          />
                        </Box>
                      )}

                      {sendable && (
                          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                          <Button
                            variant="contained"
                            onClick={submitSendTemplate}
                            endIcon={
                              <SendIcon
                                sx={{
                                  color: 'common.white',
                                }}
                              />
                            }
                            startIcon={sendingTemplate ? <CircularProgress size={20} /> : null}
                            fullWidth
                            sx={{
                              flex: 1,
                              display: 'flex',
                              transition: (theme) =>
                                theme.transitions.create(['opacity', 'transform'], {
                                  duration: theme.transitions.duration.shorter,
                                }),
                              '& .MuiButton-endIcon': {
                                transform: (theme) => (theme.direction === 'rtl' ? 'rotate(-180deg)' : undefined),
                              },
                              pointerEvents: sendingTemplate ? 'none' : null,
                              opacity: sendingTemplate ? 0.5 : 1,
                            }}
                            disabled={thisTemplateLoading}
                          >
                            {localization.singleTemplate.send}
                          </Button>
                          {chatUser?.UserDisplayPhoneNumber && scheduleLoc && (
                            <Button
                              variant="outlined"
                              onClick={(e) => setScheduleAnchor(e.currentTarget)}
                              disabled={thisTemplateLoading || sendingTemplate}
                              sx={{ minWidth: 44, px: 1 }}
                              aria-label={scheduleLoc.menuAria}
                            >
                              <ScheduleSendOutlinedIcon fontSize="small" />
                            </Button>
                          )}
                          </Box>
                        )}
                        {chatUser?.UserDisplayPhoneNumber && scheduleLoc && (
                          <ComposerScheduleMenu
                            anchorEl={scheduleAnchor}
                            open={Boolean(scheduleAnchor)}
                            onClose={() => setScheduleAnchor(null)}
                            chatUser={chatUser}
                            unboundedWindow
                            onSchedule={(at) => void submitScheduleTemplate(at)}
                            copy={{
                              title: scheduleLoc.title,
                              customPickerLabel: scheduleLoc.customPickerLabel,
                              confirm: scheduleLoc.confirm,
                              unavailable: scheduleLoc.unavailable,
                              outOfRange: scheduleLoc.outOfRange,
                              maxHint: scheduleLoc.maxHint,
                            }}
                          />
                        )}

                        {confirmApply && !sendable && selected && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleConfirmApply}
                            fullWidth
                            disabled={thisTemplateLoading}
                            sx={(theme) => ({
                              flexShrink: 0,
                              alignSelf: 'stretch',
                              minHeight: 40,
                              boxSizing: 'border-box',
                              color: theme.palette.primary.contrastText,
                              '& .MuiButton-label': {
                                width: '100%',
                                whiteSpace: 'normal',
                                lineHeight: 1.3,
                                color: 'inherit',
                              },
                            })}
                          >
                            {localization.singleTemplate.confirmApply ?? 'אישור והוספה לשלב'}
                          </Button>
                        )}
                      </Stack>
                    </Fade>
                )}
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
};

export default React.memo(MenuSingleTemplate);
